import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bt-surface-2 border border-bt-border-subtle px-3 py-2 shadow-lg">
      <p className="font-mono text-xs text-bt-text-secondary">{label}</p>
      <p className="font-mono text-sm font-medium text-bt-text-primary">
        ${payload[0].value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
      {payload[0].payload?.volume && (
        <p className="font-mono text-xs text-bt-text-tertiary mt-0.5">
          Vol: {(payload[0].payload.volume / 1e6).toFixed(1)}M
        </p>
      )}
    </div>
  );
};

export default function StockChart({ data, height = 300, color, showVolume = false, lines = [] }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] text-bt-text-tertiary font-mono text-sm" data-testid="chart-empty">
        No chart data available
      </div>
    );
  }

  const first = data[0]?.close || 0;
  const last = data[data.length - 1]?.close || 0;
  const isPositive = last >= first;
  const lineColor = color || (isPositive ? "rgb(var(--bt-positive))" : "rgb(var(--bt-negative))");
  const gradientId = `gradient-${isPositive ? "pos" : "neg"}`;
  const closes = data.map(d => d.close).filter(v => v != null);
  const all = [...closes, ...lines.map(l => l.price)];
  const lo = Math.min(...all), hi = Math.max(...all), pad = (hi - lo) * 0.06 || hi * 0.02;
  const domain = lines.length ? [+(lo - pad).toFixed(2), +(hi + pad).toFixed(2)] : ["auto", "auto"];

  return (
    <div data-testid="stock-chart" className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity={0.15} />
              <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgb(var(--bt-text-tertiary))", fontSize: 10, fontFamily: "'IBM Plex Mono'" }}
            interval="preserveStartEnd"
            minTickGap={60}
          />
          <YAxis
            domain={domain}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgb(var(--bt-text-tertiary))", fontSize: 10, fontFamily: "'IBM Plex Mono'" }}
            tickFormatter={(v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: v >= 100 ? 0 : 2 })}`}
            width={70}
          />
          {lines.map((l, i) => (
            <ReferenceLine key={i} y={l.price} stroke={l.color} strokeDasharray="6 4" strokeWidth={1.5} ifOverflow="extendDomain"
              label={{ value: `${l.label} $${l.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, position: "insideTopLeft", fill: l.color, fontSize: 10, fontFamily: "'IBM Plex Mono'" }} />
          ))}
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgb(var(--bt-border-focus))", strokeDasharray: "3 3" }} />
          <Area
            type="monotone"
            dataKey="close"
            stroke={lineColor}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            animationDuration={1200}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
